import React, { useEffect, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV, HERO, APPLY } from "@/constants/testIds";

const APPLY_URL = "https://thethinkschool.com/checkouts/qc/";
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_kiran-qc-program/artifacts/7l9b47y6_WhatsApp%20Image%202026-05-15%20at%2014.31.39.jpeg";

const openApply = () => window.open(APPLY_URL, "_blank", "noopener,noreferrer");

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

const NAV_LINKS = [
  { href: "#problems", label: "This is for you", id: "nav-link-problems" },
  { href: "#how-it-runs", label: "How it runs", id: NAV.linkRuns },
  { href: "#curriculum", label: "Curriculum", id: NAV.linkCurriculum },
  { href: "#who-gets-in", label: "Who gets in", id: NAV.linkCriteria },
  { href: "#apply", label: "Apply", id: NAV.linkApply },
];

/* ───────────────────────── NAV ───────────────────────── */
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.container}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAFAF8]/85 backdrop-blur-md border-b border-[#E5E4DE]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid={NAV.logo}
          className="font-display font-bold text-[15px] lg:text-base tracking-tight text-[#0E0F0C] flex items-center gap-2"
        >
          Think School<span className="text-[#6B6C66] mx-1">×</span>Kiran Shah
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="link-underline text-[13.5px] text-[#1A1B17] hover:text-[#1A8F68] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={NAV.applyBtn}
            onClick={openApply}
            className="hidden sm:inline-flex items-center gap-2 bg-[#0E0F0C] text-[#FAFAF8] px-5 py-2.5 rounded-full text-[13px] font-medium hover:bg-[#1A8F68] transition-colors duration-300"
          >
            Apply now <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            aria-label="menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-[#E5E4DE] text-[#0E0F0C]"
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E4DE] bg-[#FAFAF8]">
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-[15px] text-[#1A1B17]"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                openApply();
              }}
              className="mt-2 bg-[#0E0F0C] text-[#FAFAF8] px-5 py-3 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2"
              data-testid="nav-apply-button-mobile"
            >
              Apply now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

/* ───────────────────────── HERO ───────────────────────── */
const Hero = () => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 600], [0, -40]);

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="text-[11.5px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-8"
        >
          Quick Commerce Cohort · 2026
        </motion.p>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-7">
            <motion.h1
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              data-testid={HERO.headline}
              className="font-display font-semibold text-[#0E0F0C] text-[44px] leading-[1.02] sm:text-[58px] lg:text-[80px] lg:leading-[0.98] tracking-[-0.025em]"
            >
              Quick commerce is hard.
              <br />
              <span className="text-[#6B6C66]">It's harder </span>
              <span className="italic font-bold">without a mentor.</span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-8 lg:mt-10 max-w-[580px] text-[16px] lg:text-[17.5px] leading-[1.6] text-[#1A1B17]/80"
            >
              Quick commerce is new. No playbook. No senior to ping. No peer
              who's done it. You don't need more hustle. You need a mentor
              who's already built at this scale.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <button
                data-testid={HERO.applyBtn}
                onClick={openApply}
                className="group inline-flex items-center gap-3 bg-[#0E0F0C] text-[#FAFAF8] pl-7 pr-3 py-2.5 rounded-full text-[14.5px] font-medium hover:bg-[#1A8F68] transition-all duration-300"
              >
                Apply now
                <span className="w-9 h-9 grid place-items-center rounded-full bg-[#1A8F68] group-hover:bg-[#FAFAF8] group-hover:text-[#1A8F68] text-white transition-all duration-300">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
              <div className="text-[12.5px] text-[#6B6C66] tracking-wide">
                <span className="text-[#1A8F68] font-medium">30 seats</span>{" "}
                · 6 weeks · selection-based
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="lg:col-span-5"
          >
            <motion.div
              style={{ y: imgY }}
              className="relative rounded-[28px] overflow-hidden bg-[#F1EFE7] aspect-[4/5] grain"
            >
              <img
                src={HERO_IMG}
                alt="Kiran Shah and Ganesh"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute bottom-0 inset-x-0 p-5 lg:p-6 bg-gradient-to-t from-black/55 via-black/10 to-transparent">
                <div className="flex items-end justify-between gap-4 text-[#FAFAF8]">
                  <div>
                    <div className="text-[10.5px] tracking-[0.2em] uppercase opacity-80">
                      Lead Mentor
                    </div>
                    <div className="font-display text-[20px] font-semibold mt-0.5">
                      Kiran Shah
                    </div>
                    <div className="text-[12px] opacity-85">
                      Founder, Go Zero
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-[22px] font-bold">
                      ₹100Cr+
                    </div>
                    <div className="text-[10.5px] opacity-80 tracking-wider uppercase">
                      QC scale
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* deco */}
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#1A8F68]/[0.06] blur-3xl" />
    </section>
  );
};

/* ───────────────────── MENTOR STRIP ───────────────────── */
const MentorStrip = () => {
  const items = [
    "Walk in with a problem. Walk out with a decision.",
    "Work on your numbers, not generic case studies.",
    "Skip six months of guesswork.",
    "Find the leverage hiding in your funnel.",
    "Get the call right before you spend the capital.",
    "30 founders. One room. No passengers.",
  ];
  return (
    <section className="border-y border-[#E5E4DE] bg-[#F1EFE7] py-5 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0 items-center">
            {items.map((it, i) => (
              <div
                key={`${k}-${i}`}
                className="flex items-center gap-6 px-8 text-[#1A1B17]"
              >
                <span className="font-display text-[15px] font-medium tracking-tight">
                  {it}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#1A8F68]" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ───────────────────── PROBLEMS ───────────────────── */
const Problems = () => {
  const items = [
    {
      t: "Your listings need a second look",
      d: "You shipped fast. Now your listings, access and structure need a fix from someone who's seen what right looks like.",
    },
    {
      t: "Cities pulling different ways",
      d: "Double down, pull back, or hold? We'll look at your city-level numbers and make the call together.",
    },
    {
      t: "Ads that don't burn cash",
      d: "No clear ROAS bar is the fastest way to bleed. We'll set the bar and build the engine to hit it.",
    },
    {
      t: "Free the capital stuck in stock",
      d: "Inventory moves. Does your money? We'll find the rupees frozen in your supply chain and help scale better.",
    },
    {
      t: "Stop blending in on the feed",
      d: "You have a brand. We'll find the one thing that makes it impossible to scroll past on a 10-minute delivery app.",
    },
    {
      t: "Walk into the investor room ready",
      d: "You're building something fundable. We'll show you what QC investors back and how to approach them.",
    },
  ];
  return (
    <section id="problems" className="py-24 lg:py-36">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 lg:mb-20">
          <div className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-5">
              01 · This is for you if
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
              className="font-display font-semibold text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em] text-[#0E0F0C]"
            >
              Where you're stuck.
              <br />
              <span className="text-[#6B6C66]">Where we come in.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[16.5px] leading-[1.65] text-[#1A1B17]/80">
              Six walls across nearly every QC founder we meet. Not an effort
              problem. A mentor problem. This cohort helps fix that and
              accelerate your growth in a meaningful way.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E4DE]">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="bg-[#FAFAF8] p-7 lg:p-9 group cursor-default hover:bg-[#F1EFE7] transition-colors duration-500"
              data-testid={`problem-card-${i + 1}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-display text-[13px] text-[#1A8F68] tracking-wider">
                  0{i + 1}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#E5E4DE] group-hover:bg-[#1A8F68] transition-colors duration-300" />
              </div>
              <h3 className="font-display text-[20px] lg:text-[22px] font-semibold tracking-tight text-[#0E0F0C] mb-3">
                {it.t}
              </h3>
              <p className="text-[14.5px] leading-[1.6] text-[#1A1B17]/75">
                {it.d}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── HOW IT RUNS ─────────────────── */
const HowItRuns = () => {
  const features = [
    {
      tag: "Saturday",
      t: "Main session",
      d: "Framework + live diagnosis with the full cohort. Kiran-led, working through real founder numbers in real time.",
    },
    {
      tag: "Sunday",
      t: "Debrief & implementation",
      d: "Apply it to your own brand. Founder-specific. You leave the weekend with actions, not just notes.",
    },
    {
      tag: "Always-on",
      t: "1-on-1 with Kiran",
      d: "Dedicated time for your brand, your numbers, your bottlenecks. Privately, not in a group thread.",
    },
    {
      tag: "Inside the room",
      t: "Recordings · cohort only",
      d: "Every session recorded and shared with the 30 founders. Nothing shipped outside the cohort.",
    },
  ];
  return (
    <section id="how-it-runs" className="py-24 lg:py-36 bg-[#F1EFE7]">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-8 mb-14">
          <div className="max-w-[680px]">
            <p className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-5">
              02 · How it runs
            </p>
            <h2 className="font-display font-semibold text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
              Not a course. <span className="text-[#6B6C66]">A co-pilot.</span>
            </h2>
            <div className="mt-6 text-[16.5px] leading-[1.65] text-[#1A1B17]/80 max-w-[640px]">
              <p>Kiran has scaled a brand to ₹100 Cr+ on quick commerce.</p>
              <p>
                For six weekends, he's on your actual numbers. In the group.
                In the Sunday debrief. In your <span className="whitespace-nowrap">1-on-1</span>.
              </p>
              <p>Not theory. Not case studies. Your business.</p>
            </div>
          </div>
          <div className="font-display text-right">
            <div className="text-[80px] leading-none font-bold text-[#1A8F68]">
              30
            </div>
            <div className="text-[11.5px] tracking-[0.18em] uppercase text-[#1A1B17]/70 mt-2">
              peer founders<br />in the room
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-px bg-[#E5E4DE] border border-[#E5E4DE]">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="bg-[#FAFAF8] p-8 lg:p-10"
              data-testid={`runs-card-${i + 1}`}
            >
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase text-[#1A8F68] font-medium mb-6">
                <span className="w-6 h-px bg-[#1A8F68]" />
                {f.tag}
              </div>
              <h3 className="font-display text-[24px] lg:text-[28px] font-semibold tracking-tight text-[#0E0F0C] mb-3">
                {f.t}
              </h3>
              <p className="text-[15px] leading-[1.65] text-[#1A1B17]/75 max-w-[440px]">
                {f.d}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── CURRICULUM ─────────────────── */
const Curriculum = () => {
  const weeks = [
    { n: "01", t: "Listing, platform access & org clarity", s: "Fix the foundation" },
    { n: "02", t: "City strategy & unit economics", s: "Stop spreading thin" },
    { n: "03", t: "Ad spend & ROAS", s: "Know what you're buying" },
    { n: "04", t: "Inventory & capital flow", s: "Find the stuck money" },
    { n: "05", t: "Brand positioning & differentiation", s: "Stand out on the shelf" },
    { n: "06", t: "Fundraising & what's next", s: "Scale with conviction" },
  ];
  return (
    <section id="curriculum" className="py-24 lg:py-36">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 lg:mb-20">
          <div className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-5">
              03 · Curriculum
            </p>
            <h2 className="font-display font-semibold text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em] text-[#0E0F0C]">
              6 Weeks.
              <br />
              <span className="text-[#1A8F68] italic font-medium">A to Z</span> of
              <br />
              Quick Commerce.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[16.5px] leading-[1.65] text-[#1A1B17]/80">
              Every week. One lever of the QC engine. Show up with a problem.
              Leave with a decision. No theory. No filler. Just the moves that
              move the number.
            </p>
          </div>
        </div>

        <div className="border-t border-[#E5E4DE]">
          {weeks.map((w, i) => (
            <motion.a
              href="#apply"
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="group grid grid-cols-12 items-center gap-4 lg:gap-8 py-7 lg:py-9 border-b border-[#E5E4DE] hover:bg-[#F1EFE7] transition-colors duration-500 px-2"
              data-testid={`week-${i + 1}`}
            >
              <div className="col-span-2 lg:col-span-1 font-display text-[18px] lg:text-[22px] font-bold text-[#1A8F68]">
                W{Number(w.n)}
              </div>
              <div className="col-span-10 lg:col-span-7">
                <h3 className="font-display text-[22px] lg:text-[30px] font-semibold tracking-tight text-[#0E0F0C] group-hover:translate-x-1 transition-transform duration-500">
                  {w.t}
                </h3>
              </div>
              <div className="col-span-10 col-start-3 lg:col-span-3 lg:col-start-9 text-[14px] text-[#6B6C66] italic">
                {w.s}
              </div>
              <div className="hidden lg:flex col-span-1 justify-end">
                <ArrowRight className="w-5 h-5 text-[#1A1B17] opacity-30 group-hover:opacity-100 group-hover:text-[#1A8F68] group-hover:translate-x-1 transition-all duration-500" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── WHO GETS IN ─────────────────── */
const WhoGetsIn = () => {
  const criteria = [
    "Already live on at least one QC platform, or planning to list in the next 3–6 months.",
    "Revenue between ₹15L and ₹80L a month, with at least 2 months of live sales data.",
    "Building in a category with a market size of ₹50 Cr or more.",
    "QC is a priority channel for you, not a side bet.",
    "You're the founder or QC head. Someone who can make the call and act on it.",
    "You can show up both days every weekend for 6 weeks.",
  ];
  return (
    <section id="who-gets-in" className="py-24 lg:py-36 bg-[#F1EFE7]">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <p className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-5">
            04 · Who gets in
          </p>
          <h2 className="font-display font-semibold text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em] max-w-[820px]">
            Built for founders who are already in motion.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-6 lg:gap-y-8">
          {criteria.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="flex items-start gap-5 border-b border-[#D9D7CE] pb-6"
              data-testid={`criteria-${i + 1}`}
            >
              <div className="shrink-0 w-9 h-9 grid place-items-center rounded-full bg-[#1A8F68] text-[#FAFAF8] mt-0.5">
                <Check className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <p className="text-[15.5px] lg:text-[16.5px] leading-[1.55] text-[#1A1B17]">
                {c}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── HOW TO APPLY ─────────────────── */
const HowToApply = () => {
  const steps = [
    {
      n: "01",
      t: "Pay ₹5,000 & fill your application",
      d: "Fully refundable. A detailed application covering your brand, platform presence, revenue and the specific problems you want to work on.",
    },
    {
      n: "02",
      t: "We review & select",
      d: "Every application is read individually. Selection-based, not first-come-first-served. If it's not a fit, the ₹5,000 is refunded. No questions.",
    },
    {
      n: "03",
      t: "15-minute call with our team",
      d: "We walk you through the cohort in detail, answer questions, and confirm the fit from both sides before you commit.",
    },
    {
      n: "04",
      t: "Cohort begins",
      d: "Saturday and Sunday every week for 6 weeks. 30 founders in the room. No passengers.",
    },
  ];
  return (
    <section id="how-to-apply" className="py-24 lg:py-36">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="mb-14 max-w-[820px]">
          <p className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-5">
            05 · How to apply
          </p>
          <h2 className="font-display font-semibold text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
            Four steps in.{" "}
            <span className="text-[#6B6C66]">Zero pressure.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 lg:gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              className="relative"
              data-testid={`apply-step-${i + 1}`}
            >
              <div className="font-display text-[68px] lg:text-[84px] font-bold leading-none text-[#1A8F68]/15 mb-4">
                {s.n}
              </div>
              <h3 className="font-display text-[20px] lg:text-[22px] font-semibold tracking-tight text-[#0E0F0C] mb-3">
                {s.t}
              </h3>
              <p className="text-[14.5px] leading-[1.6] text-[#1A1B17]/75">
                {s.d}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── FINAL CTA ─────────────────── */
const FinalCta = () => {
  return (
    <section
      id="apply"
      className="relative bg-[#0E0F0C] text-[#FAFAF8] grain overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 w-[460px] h-[460px] rounded-full bg-[#1A8F68]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1A8F68]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-[11px] tracking-[0.22em] text-[#1A8F68] font-medium uppercase mb-8"
        >
          06 · Final call
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-display font-semibold text-[40px] sm:text-[58px] lg:text-[88px] leading-[0.98] tracking-[-0.025em] max-w-[1080px]"
        >
          Are you one of the{" "}
          <span className="text-[#1A8F68]">30 QC founders</span> we're looking
          for?
        </motion.h2>

        <div className="mt-12 lg:mt-16 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-[16.5px] lg:text-[18px] leading-[1.6] text-[#FAFAF8]/75 max-w-[560px]">
              Seats capped at 30. Selection-based. Pay ₹5,000 to apply.
              Refunded in full if you're not selected.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <button
                onClick={openApply}
                data-testid={APPLY.ctaFinal}
                className="group inline-flex items-center gap-3 bg-[#1A8F68] text-[#FAFAF8] pl-8 pr-3 py-3 rounded-full text-[15px] font-medium hover:bg-[#FAFAF8] hover:text-[#0E0F0C] transition-all duration-300"
              >
                Apply now
                <span className="w-10 h-10 grid place-items-center rounded-full bg-[#0E0F0C] text-[#FAFAF8] group-hover:bg-[#1A8F68] transition-all duration-300">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
              <span className="text-[12.5px] tracking-wider text-[#FAFAF8]/55 uppercase">
                Cohort begins · 2026
              </span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[#FAFAF8]/12 rounded-2xl p-6 lg:p-8 bg-[#FAFAF8]/[0.03] backdrop-blur-sm">
              <div className="flex items-baseline justify-between mb-6">
                <span className="text-[11px] tracking-[0.18em] uppercase text-[#FAFAF8]/55">
                  Apply
                </span>
                <span className="font-display text-[28px] lg:text-[32px] font-semibold text-[#FAFAF8]">
                  ₹5,000
                </span>
              </div>
              <p className="text-[13.5px] text-[#FAFAF8]/65 leading-relaxed mb-6">
                Fully refundable if you're not selected. Applies toward your
                cohort fee on acceptance.
              </p>
              <div className="h-px bg-[#FAFAF8]/15 my-6" />
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] tracking-[0.18em] uppercase text-[#FAFAF8]/55">
                  Total cohort fee
                </span>
                <span className="font-display text-[20px] font-semibold text-[#FAFAF8]/85">
                  ₹3,00,000
                </span>
              </div>
              <p className="text-[12.5px] text-[#FAFAF8]/55 mt-3">
                Payable only after selection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── FOOTER ─────────────────── */
const Footer = () => (
  <footer className="bg-[#0E0F0C] text-[#FAFAF8] border-t border-[#FAFAF8]/10">
    <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-10 lg:py-12 flex flex-wrap items-center justify-between gap-5">
      <div className="font-display text-[14px] font-medium flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-[#1A8F68]" />
        Think School × Kiran Shah · QC Cohort 2026
      </div>
      <div className="flex flex-wrap items-center gap-5 text-[12.5px] text-[#FAFAF8]/55">
        <span>
          Questions?{" "}
          <a
            href="mailto:help@thethinkschool.com"
            className="text-[#FAFAF8]/85 hover:text-[#1A8F68] transition-colors link-underline"
            data-testid="footer-contact-email"
          >
            help@thethinkschool.com
          </a>
        </span>
        <span className="hidden sm:inline text-[#FAFAF8]/25">·</span>
        <span>© 2026 · All rights reserved.</span>
      </div>
    </div>
  </footer>
);

/* ─────────────────── APP ─────────────────── */
function App() {
  return (
    <div className="App font-body">
      <Nav />
      <main>
        <Hero />
        <MentorStrip />
        <Problems />
        <HowItRuns />
        <Curriculum />
        <WhoGetsIn />
        <HowToApply />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

export default App;
